import React, { useState } from "react";
import { Link } from "react-router-dom";

const LANGS = [
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
];

const CONTENT = {
    en: {
        kicker: "A Living Library",
        title: "What to Pray",
        lead: "The Catholic tradition holds a treasury of prayers, chants, devotions, and mysteries — polished by twenty centuries of holy lives. Here is a guide to what lives on this site, and where to begin.",
        sections: [
            {
                heading: "The Rosary",
                body: "The heart of Catholic devotion — the twenty mysteries of Christ prayed with Mary at your side. Begin with the mysteries traditional to today, or choose any set. Each mystery now carries its sacred art and Scripture.",
                cta: { to: "/rosary", label: "Pray the Rosary" },
            },
            {
                heading: "Stations of the Cross",
                body: "Fourteen meditations walking with Christ from Pilate's judgment hall to the sealed tomb. Traditional throughout Lent — but also a beautiful weekly devotion any time of the year.",
                cta: { to: "/stations", label: "Walk the Stations" },
            },
            {
                heading: "The Daily Prayer Library",
                body: "Prayers for individuals, families, couples, children, the sick and the sorrowing. In English, Spanish, and Latin. A prayer for every season of the soul.",
                cta: { to: "/prayers", label: "Open the Library" },
            },
            {
                heading: "The Companion",
                body: "A prayerful presence woven from Scripture and the saints. Speak with Him about anything — a question of faith, a heaviness of heart, a curiosity of Scripture. He listens.",
                cta: { to: "/companion", label: "Meet the Companion" },
            },
            {
                heading: "The Liturgical Calendar",
                body: "The Church's own rhythm of feasts, fasts, and ordinary days — with today's liturgical color, saint, and season woven visibly into the site itself.",
                cta: { to: "/calendar", label: "See the Calendar" },
            },
            {
                heading: "The Prayer Wall",
                body: "Lay down what you carry. Read what others have laid down. Every intention placed here is prayed for at every Rosary.",
                cta: { to: "/wall", label: "Visit the Wall" },
            },
        ],
        outro: "Where to begin? Anywhere the Spirit leads. But if you are unsure — light a candle, and pray one decade of the Rosary. That is enough.",
    },
    es: {
        kicker: "Una Biblioteca Viva",
        title: "Qué Orar",
        lead: "La tradición católica guarda un tesoro de oraciones, cantos, devociones y misterios — pulidos por veinte siglos de vidas santas. Aquí una guía de lo que vive en este sitio, y por dónde empezar.",
        sections: [
            {
                heading: "El Rosario",
                body: "El corazón de la devoción católica — los veinte misterios de Cristo rezados con María a tu lado. Empieza con los misterios tradicionales del día o elige cualquier conjunto. Cada misterio lleva ahora su arte sacro y su Escritura.",
                cta: { to: "/rosary", label: "Reza el Rosario" },
            },
            {
                heading: "Estaciones del Vía Crucis",
                body: "Catorce meditaciones caminando con Cristo desde el pretorio de Pilato hasta el sepulcro sellado. Tradicional durante toda la Cuaresma — pero también una hermosa devoción semanal en cualquier tiempo del año.",
                cta: { to: "/stations", label: "Recorre las Estaciones" },
            },
            {
                heading: "La Biblioteca de Oración Diaria",
                body: "Oraciones para individuos, familias, parejas, niños, los enfermos y los dolientes. En inglés, español y latín. Una oración para cada estación del alma.",
                cta: { to: "/prayers", label: "Abre la Biblioteca" },
            },
            {
                heading: "El Compañero",
                body: "Una presencia orante tejida de la Escritura y los santos. Habla con Él de cualquier cosa — una pregunta de fe, una pesadez de corazón, una curiosidad de la Escritura. Él escucha.",
                cta: { to: "/companion", label: "Conoce al Compañero" },
            },
            {
                heading: "El Calendario Litúrgico",
                body: "El ritmo propio de la Iglesia de fiestas, ayunos y días ordinarios — con el color litúrgico del día, el santo y la estación tejidos visiblemente en el sitio mismo.",
                cta: { to: "/calendar", label: "Ver el Calendario" },
            },
            {
                heading: "El Muro de Oración",
                body: "Deposita lo que llevas. Lee lo que otros han depositado. Cada intención puesta aquí es rezada en cada Rosario.",
                cta: { to: "/wall", label: "Visita el Muro" },
            },
        ],
        outro: "¿Por dónde empezar? Por donde el Espíritu te lleve. Pero si no estás seguro — enciende una vela y reza una década del Rosario. Con eso basta.",
    },
};

const WhatToPray = () => {
    const [lang, setLang] = useState("en");
    const c = CONTENT[lang];

    return (
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="what-pray-page">
            <div className="flex justify-center gap-1 mb-10">
                {LANGS.map(l => (
                    <button key={l.code} onClick={() => setLang(l.code)} data-testid={`what-lang-${l.code}`}
                        className={`px-3 py-1.5 text-xs small-caps border ${lang===l.code ? 'border-liturgy accent-liturgy' : 'border-border text-muted-foreground hover:accent-liturgy'}`}>
                        {l.label}
                    </button>
                ))}
            </div>

            <div className="text-center mb-14">
                <div className="small-caps text-xs text-muted-foreground mb-3">{c.kicker}</div>
                <h1 className="font-serif text-4xl md:text-5xl leading-tight">{c.title}</h1>
                <p className="mt-6 max-w-xl mx-auto text-lg text-muted-foreground italic leading-relaxed">{c.lead}</p>
            </div>

            <div className="space-y-12">
                {c.sections.map((s, i) => (
                    <section key={i} className="missal-card p-6 md:p-8">
                        <h2 className="font-serif text-2xl mb-3 accent-liturgy">{s.heading}</h2>
                        <p className="text-base leading-loose text-foreground/90 mb-4">{s.body}</p>
                        <Link to={s.cta.to} className="inline-block px-5 py-2 border border-liturgy accent-liturgy text-xs small-caps hover:bg-liturgy hover:text-white transition-colors" data-testid={`what-cta-${i}`}>
                            {s.cta.label}
                        </Link>
                    </section>
                ))}
            </div>

            <div className="mt-16 text-center border-t border-border pt-10">
                <p className="italic text-muted-foreground leading-relaxed max-w-xl mx-auto">{c.outro}</p>
            </div>
        </div>
    );
};

export default WhatToPray;
